import React from "react";
import { node, string, number } from "prop-types";
import Heading from "../Heading";
import Button from "../Button";
import Icon from "../Icon";
import SimpleTemplate from "../SimpleTemplate";
import Table from "../Table";
import Tbody from "../Table/Tbody";
import { classes } from "../utils/classes";
import Paragraph from "../Paragraph";
import { Col, Container, Row } from "../Grid";
import Section from "../Section";
import { sxProps, sxPropTypes } from "../props/sx";
import { spacingProps, spacingPropTypes } from "../props/spacing";
import pureProps from "../utils/pureProps";

export const ViewerPageTemplateSection = ({ children, className, sm, md, lg }) => {
	return (
		<Section>
			<Container>
				<Row mx px utility={{ justify: "center" }}>
					<Col sm={sm} md={md} lg={lg} className={classes(className)}>
						{children}
					</Col>
				</Row>
			</Container>
		</Section>
	);
};

ViewerPageTemplateSection.propTypes = {
	children: node,
	className: string,
	sm: number,
	md: number,
	lg: number,
};

export const ViewerPageTemplateSectionTitle = ({ sx = { ...sxProps }, children, ...props }) => {
	return (
		<Paragraph
			sx={{ fontSize: "xl", fontWeight: "normal", textAlign: "center", ...pureProps(sx) }}
			{...props}
		>
			{children}
		</Paragraph>
	);
};

ViewerPageTemplateSectionTitle.propTypes = {
	sx: sxPropTypes,
	children: node.isRequired,
};

export const ViewerPageTemplateSectionSubTitle = ({
	sx = { ...sxProps },
	children,
	className = "",
}) => {
	return (
		<Paragraph
			className={classes(className)}
			sx={{ fontSize: "m", fontWeight: "normal", textAlign: "center", ...pureProps(sx) }}
		>
			{children}
		</Paragraph>
	);
};

ViewerPageTemplateSectionSubTitle.propTypes = {
	sx: sxPropTypes,
	className: string,
	children: node.isRequired,
};

export const ViewerPageTemplateTablesSection = ({ children }) => {
	return (
		<Section spacing={{ mb: 20 }}>
			<Container>
				<Row mx px utility={{ justify: "center" }}>
					<Col md={12} lg={12}>
						{children}
					</Col>
				</Row>
			</Container>
		</Section>
	);
};

ViewerPageTemplateTablesSection.propTypes = {
	children: node.isRequired,
};

export const ViewerPageTemplateTableSectionTitle = ({ children }) => {
	return (
		<Paragraph sx={{ fontSize: "m", fontWeight: "bold", textAlign: "center" }}>
			{children}
		</Paragraph>
	);
};

ViewerPageTemplateTableSectionTitle.propTypes = {
	children: node.isRequired,
};

export const ViewerPageTemplateTableSectionSubTitle = ({ children }) => {
	return (
		<Paragraph sx={{ fontSize: "m", fontWeight: "normal", textAlign: "center" }}>
			{children}
		</Paragraph>
	);
};

ViewerPageTemplateTableSectionSubTitle.propTypes = {
	children: node.isRequired,
};

export const ViewerPageTemplateTableSectionText = ({ children }) => {
	return (
		<Paragraph sx={{ fontSize: "m", fontWeight: "normal" }} spacing={{ px: 2, m: 0 }}>
			{children}
		</Paragraph>
	);
};

ViewerPageTemplateTableSectionText.propTypes = {
	children: node.isRequired,
};

export const ViewerPageTemplateTableWrapper = ({ children }) => {
	return (
		<Row mx px utility={{ justify: "center" }}>
			<Col sm={12} md={12} lg={12}>
				<Row mx px utility={{ justify: "center" }}>
					{children}
				</Row>
			</Col>
		</Row>
	);
};

ViewerPageTemplateTableWrapper.propTypes = {
	children: node.isRequired,
};

export const ViewerPageTemplateTable = ({ children }) => {
	return (
		<Table isBottomBorderedStriped>
			<Tbody>{children}</Tbody>
		</Table>
	);
};

ViewerPageTemplateTable.propTypes = {
	children: node.isRequired,
};

export const ViewerPageTemplateSimpleTemplate = ({ children, ...props }) => {
	return (
		<SimpleTemplate utility={{ bgColor: "white" }} panelPaddingY={8} {...props}>
			{children}
		</SimpleTemplate>
	);
};

ViewerPageTemplateSimpleTemplate.propTypes = {
	children: node.isRequired,
};

export const ViewerPageTemplateSimpleTemplateText = ({
	size = "xxxl",
	weight = "normal",
	sx = { ...sxProps },
	spacing = { ...spacingProps },
	...props
}) => {
	return (
		<Paragraph
			sx={{ fontSize: size, fontWeight: weight, textAlign: "center", ...pureProps(sx) }}
			spacing={{ mb: 4, ...pureProps(spacing) }}
			{...props}
		/>
	);
};

ViewerPageTemplateSimpleTemplateText.propTypes = {
	sx: sxPropTypes,
	spacing: spacingPropTypes,
	...Paragraph.propTypes,
};

export const ViewerPageTemplateHeading = ({ sx = { ...sxProps }, size = "xl", ...props }) => {
	return (
		<Heading
			Tag="h2"
			sx={{ fontSize: size, textAlign: "center", ...pureProps(sx) }}
			spacing={{ mt: 14 }}
			{...props}
		/>
	);
};

ViewerPageTemplateHeading.propTypes = {
	sx: sxPropTypes,
	...Heading.propTypes,
};

export const ViewerPageTemplateDownloadWrapper = ({ children }) => {
	return (
		<Row utility={{ justify: "center" }}>
			<Col md={11} lg={10}>
				{children}
			</Col>
		</Row>
	);
};

ViewerPageTemplateDownloadWrapper.propTypes = {
	children: node.isRequired,
};

export const ViewerPageTemplateDownloadRow = ({ children }) => {
	return <Row mx>{children}</Row>;
};

ViewerPageTemplateDownloadRow.propTypes = {
	children: node.isRequired,
};

export const ViewerPageTemplateDownloadItem = ({ children }) => {
	return (
		<Col md={6} spacing={{ mb: 6 }}>
			{children}
		</Col>
	);
};

ViewerPageTemplateDownloadItem.propTypes = {
	children: node.isRequired,
};

export const ViewerPageTemplateDownloadItemLink = ({ children, href, icon, ...props }) => {
	return (
		<Button.Link href={href} size="s" isBlock className="px-0" {...props}>
			<Icon name={icon} /> {children}
		</Button.Link>
	);
};

ViewerPageTemplateDownloadItemLink.propTypes = {
	children: node.isRequired,
	href: string.isRequired,
	icon: string.isRequired,
};
